/**
 * Block type registry: maps block types to editors, renderers, and default content.
 *
 * This is the central registry for the block editor. NOS-25 (BlockEditor) uses it
 * to determine which editor component to show; NOS-29 (BlockRenderer) uses it for
 * public site rendering.
 *
 * @see docs/specs/MULTI_PAGE_WEBSITE_BUILDER.md
 * @see src/components/website-builder/blocks/types.ts
 */

import type {
  BlockType,
  BlockContentMap,
  HeroBlockContent,
  ImpactStatsBlockContent,
  DonationCTABlockContent,
  FooterBlockContent,
  ParagraphBlockContent,
  HeadingBlockContent,
  ImageBlockContent,
  TeamMembersBlockContent,
  TimelineBlockContent,
  FormBlockContent,
  TestimonialsBlockContent,
  VolunteerSignupBlockContent,
  GalleryBlockContent,
  ContactBlockContent,
  AccordionBlockContent,
  CardsBlockContent,
  LogoGridBlockContent,
  TableBlockContent,
  FundraisingGoalBlockContent,
  ListBlockContent,
  BlockquoteBlockContent,
  VideoBlockContent,
  CalloutBlockContent,
  ImageTextBlockContent,
  PullQuoteBlockContent,
  DividerBlockContent,
  ButtonBlockContent,
  FileDownloadBlockContent,
  EmbedBlockContent,
  NewsletterSignupBlockContent,
  AuthorBioBlockContent,
  MapBlockContent,
  DonationFormBlockContent,
  CampaignBannerBlockContent,
  LatestPostsBlockContent,
} from "./types"

/** Icon identifier (Lucide icon name). Resolved by block picker / UI. */
export type BlockIconName =
  | "Layout"
  | "FileText"
  | "BarChart3"
  | "Heart"
  | "Users"
  | "Footprints"
  | "Type"
  | "Heading"
  | "Image"
  | "Clock"
  | "FormInput"
  | "List"
  | "Quote"
  | "Video"
  | "MapPin"
  | "Download"
  | "Code2"
  | "Mail"
  | "PanelTop"
  | "Megaphone"

export interface BlockTypeConfig<T extends BlockType = BlockType> {
  /** Display label in block picker and section list */
  label: string
  /** Lucide icon name for picker/list UI */
  icon: BlockIconName
  /** Editor component identifier. NOS-27 implements these. */
  editorComponent: string
  /** Renderer component identifier. NOS-29 implements these. */
  rendererComponent: string
  /** Default content when creating a new block of this type */
  defaultContent: BlockContentMap[T]
  /** Optional: category for block picker grouping */
  category?: "layout" | "content" | "social" | "cta" | "form"
}

/** Registry: block type -> config */
const BLOCK_REGISTRY: Record<BlockType, BlockTypeConfig> = {
  hero: {
    label: "Hero",
    icon: "Layout",
    editorComponent: "HeroBlockEditor",
    rendererComponent: "HeroSection",
    category: "layout",
    defaultContent: {
      headline: "",
      subheadline: "",
      cta_text: "Donate now",
      background_image: undefined,
    } satisfies HeroBlockContent,
  },
  impact_stats: {
    label: "Impact Stats",
    icon: "BarChart3",
    editorComponent: "ImpactStatsBlockEditor",
    rendererComponent: "ImpactStatsSection",
    category: "content",
    defaultContent: {
      heading: "Our impact",
      stats: [
        { label: "Total Raised", value: "$0" },
        { label: "Supporters", value: "0" },
        { label: "Years Active", value: "0" },
      ],
      columns: 3,
    } satisfies ImpactStatsBlockContent,
  },
  donation_cta: {
    label: "Donation CTA",
    icon: "Heart",
    editorComponent: "DonationCTABlockEditor",
    rendererComponent: "DonationCTASection",
    category: "cta",
    defaultContent: {
      heading: "Support our mission",
      body: "",
      cta_text: "Donate now",
    } satisfies DonationCTABlockContent,
  },
  testimonials: {
    label: "Testimonials",
    icon: "Users",
    editorComponent: "TestimonialsBlockEditor",
    rendererComponent: "TestimonialsSection",
    category: "content",
    defaultContent: {
      heading: "What people say",
      testimonials: [
        { quote: "This organization changed my life.", author: "Jane D.", role: "Beneficiary" },
        { quote: "Proud to support such meaningful work.", author: "John S.", role: "Donor" },
      ],
      columns: 2,
    } satisfies TestimonialsBlockContent,
  },
  volunteer_signup: {
    label: "Volunteer Signup",
    icon: "Footprints",
    editorComponent: "VolunteerSignupBlockEditor",
    rendererComponent: "VolunteerSignupSection",
    category: "cta",
    defaultContent: {
      heading: "Join us as a volunteer",
      body: "Help us make a difference in your community.",
      cta_text: "Sign up to volunteer",
    } satisfies VolunteerSignupBlockContent,
  },
  gallery: {
    label: "Gallery",
    icon: "Image",
    editorComponent: "GalleryBlockEditor",
    rendererComponent: "GallerySection",
    category: "content",
    defaultContent: {
      heading: "Our work in action",
      images: [
        { url: undefined, alt: "", caption: undefined },
        { url: undefined, alt: "", caption: undefined },
        { url: undefined, alt: "", caption: undefined },
      ],
      columns: 3,
    } satisfies GalleryBlockContent,
  },
  contact: {
    label: "Contact",
    icon: "FormInput",
    editorComponent: "ContactBlockEditor",
    rendererComponent: "ContactSection",
    category: "form",
    defaultContent: {
      heading: "Get in touch",
      body: "",
      fields: [
        { name: "name", type: "text", label: "Name", required: true },
        { name: "email", type: "email", label: "Email", required: true },
        { name: "message", type: "textarea", label: "Message", required: true },
      ],
    } satisfies ContactBlockContent,
  },
  footer: {
    label: "Footer",
    icon: "Footprints",
    editorComponent: "FooterBlockEditor",
    rendererComponent: "FooterSection",
    category: "layout",
    defaultContent: {
      tagline: "Creating change together.",
      social_links: [],
    } satisfies FooterBlockContent,
  },
  paragraph: {
    label: "Paragraph",
    icon: "Type",
    editorComponent: "ParagraphBlockEditor",
    rendererComponent: "ParagraphBlockRenderer",
    category: "content",
    defaultContent: {
      content: null,
    } satisfies ParagraphBlockContent,
  },
  heading: {
    label: "Heading",
    icon: "Heading",
    editorComponent: "HeadingBlockEditor",
    rendererComponent: "HeadingBlockRenderer",
    category: "content",
    defaultContent: {
      text: "",
      level: 2,
    } satisfies HeadingBlockContent,
  },
  image: {
    label: "Image",
    icon: "Image",
    editorComponent: "ImageBlockEditor",
    rendererComponent: "ImageBlockRenderer",
    category: "content",
    defaultContent: {
      url: undefined,
      alt: "",
      caption: undefined,
    } satisfies ImageBlockContent,
  },
  team_members: {
    label: "Team Members",
    icon: "Users",
    editorComponent: "TeamMembersBlockEditor",
    rendererComponent: "TeamMembersBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "Meet our team",
      members: [],
      layout: "grid",
      columns: 3,
    } satisfies TeamMembersBlockContent,
  },
  timeline: {
    label: "Timeline",
    icon: "Clock",
    editorComponent: "TimelineBlockEditor",
    rendererComponent: "TimelineBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "Our story",
      items: [],
      layout: "vertical",
    } satisfies TimelineBlockContent,
  },
  form: {
    label: "Contact Form",
    icon: "FormInput",
    editorComponent: "FormBlockEditor",
    rendererComponent: "FormBlockRenderer",
    category: "form",
    defaultContent: {
      heading: "Get in touch",
      submit_label: "Send message",
      fields: [
        { name: "name", type: "text", label: "Name", required: true },
        { name: "email", type: "email", label: "Email", required: true },
        { name: "message", type: "textarea", label: "Message", required: true },
      ],
    } satisfies FormBlockContent,
  },
  accordion: {
    label: "Accordion",
    icon: "List",
    editorComponent: "AccordionBlockEditor",
    rendererComponent: "AccordionBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "Frequently asked questions",
      items: [{ question: "", answer: "" }],
      columns: 1,
    } satisfies AccordionBlockContent,
  },
  cards: {
    label: "Cards",
    icon: "Layout",
    editorComponent: "CardsBlockEditor",
    rendererComponent: "CardsBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "",
      subheading: "",
      items: [{ title: "", body: "", cta_text: "", cta_url: "" }],
      columns: 3,
    } satisfies CardsBlockContent,
  },
  logo_grid: {
    label: "Logo Grid",
    icon: "Image",
    editorComponent: "LogoGridBlockEditor",
    rendererComponent: "LogoGridBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "Our partners",
      subheading: "",
      logos: [{ name: "", image_url: "", url: "" }],
      columns: 4,
    } satisfies LogoGridBlockContent,
  },
  table: {
    label: "Table",
    icon: "PanelTop",
    editorComponent: "TableBlockEditor",
    rendererComponent: "TableBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "",
      headers: ["", ""],
      rows: [["", ""]],
    } satisfies TableBlockContent,
  },
  fundraising_goal: {
    label: "Fundraising Goal",
    icon: "Heart",
    editorComponent: "FundraisingGoalBlockEditor",
    rendererComponent: "FundraisingGoalBlockRenderer",
    category: "cta",
    defaultContent: {
      heading: "Support our cause",
      goal_amount: 10000,
      raised_amount: 0,
      currency: "USD",
      label: "raised",
      cta_text: "Donate now",
      cta_url: "",
    } satisfies FundraisingGoalBlockContent,
  },
  list: {
    label: "List",
    icon: "List",
    editorComponent: "ListBlockEditor",
    rendererComponent: "ListBlockRenderer",
    category: "content",
    defaultContent: {
      list_type: "unordered",
      items: [""],
      heading: "",
      columns: 1,
    } satisfies ListBlockContent,
  },
  blockquote: {
    label: "Blockquote",
    icon: "Quote",
    editorComponent: "BlockquoteBlockEditor",
    rendererComponent: "BlockquoteBlockRenderer",
    category: "content",
    defaultContent: {
      quote: "",
      attribution: "",
      source: "",
    } satisfies BlockquoteBlockContent,
  },
  video: {
    label: "Video",
    icon: "Video",
    editorComponent: "VideoBlockEditor",
    rendererComponent: "VideoBlockRenderer",
    category: "content",
    defaultContent: {
      url: "",
      caption: "",
      autoplay: false,
    } satisfies VideoBlockContent,
  },
  callout: {
    label: "Callout",
    icon: "PanelTop",
    editorComponent: "CalloutBlockEditor",
    rendererComponent: "CalloutBlockRenderer",
    category: "content",
    defaultContent: {
      type: "info",
      heading: "",
      body: "",
    } satisfies CalloutBlockContent,
  },
  image_text: {
    label: "Image + Text",
    icon: "Image",
    editorComponent: "ImageTextBlockEditor",
    rendererComponent: "ImageTextBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "Our mission",
      body: "",
      image_url: undefined,
      image_position: "left",
      cta_text: "",
      cta_url: "",
    } satisfies ImageTextBlockContent,
  },
  pull_quote: {
    label: "Pull Quote",
    icon: "Quote",
    editorComponent: "PullQuoteBlockEditor",
    rendererComponent: "PullQuoteBlockRenderer",
    category: "content",
    defaultContent: {
      quote: "",
      attribution: "",
    } satisfies PullQuoteBlockContent,
  },
  divider: {
    label: "Divider",
    icon: "PanelTop",
    editorComponent: "DividerBlockEditor",
    rendererComponent: "DividerBlockRenderer",
    category: "content",
    defaultContent: {
      style: "line",
    } satisfies DividerBlockContent,
  },
  button: {
    label: "Button",
    icon: "Layout",
    editorComponent: "ButtonBlockEditor",
    rendererComponent: "ButtonBlockRenderer",
    category: "cta",
    defaultContent: {
      text: "Learn more",
      url: "",
      variant: "primary",
      align: "left",
    } satisfies ButtonBlockContent,
  },
  file_download: {
    label: "File Download",
    icon: "Download",
    editorComponent: "FileDownloadBlockEditor",
    rendererComponent: "FileDownloadBlockRenderer",
    category: "content",
    defaultContent: {
      label: "",
      url: "",
      description: "",
      file_type: "",
      file_size: "",
    } satisfies FileDownloadBlockContent,
  },
  embed: {
    label: "Embed",
    icon: "Code2",
    editorComponent: "EmbedBlockEditor",
    rendererComponent: "EmbedBlockRenderer",
    category: "content",
    defaultContent: {
      url: "",
      title: "",
      height: 400,
    } satisfies EmbedBlockContent,
  },
  newsletter_signup: {
    label: "Newsletter Signup",
    icon: "Mail",
    editorComponent: "NewsletterSignupBlockEditor",
    rendererComponent: "NewsletterSignupBlockRenderer",
    category: "cta",
    defaultContent: {
      heading: "Subscribe to our newsletter",
      body: "",
      cta_text: "Subscribe",
      placeholder: "Enter your email",
    } satisfies NewsletterSignupBlockContent,
  },
  author_bio: {
    label: "Author Info",
    icon: "Users",
    editorComponent: "AuthorBioBlockEditor",
    rendererComponent: "AuthorBioBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "About the Author",
      authors: [{ name: "", role: "", bio: "", image_url: undefined, social_links: [] }],
    } satisfies AuthorBioBlockContent,
  },
  map: {
    label: "Map",
    icon: "MapPin",
    editorComponent: "MapBlockEditor",
    rendererComponent: "MapBlockRenderer",
    category: "content",
    defaultContent: {
      address: "",
      heading: "",
      height: 300,
    } satisfies MapBlockContent,
  },
  // NOS-162: conversion blocks (research §C) — edited via SectionContextPanel fields
  donation_form: {
    label: "Donation Form",
    icon: "Heart",
    editorComponent: "SectionContextPanel",
    rendererComponent: "DonationFormBlock",
    category: "cta",
    defaultContent: {
      heading: "Make a difference today",
      body: "Your gift goes to work immediately.",
      tiers: [{ amount: 25 }, { amount: 50 }, { amount: 100 }],
      allow_custom: true,
      trust_text: "Tax-deductible · Secure payment",
      cta_text: "Donate",
    } satisfies DonationFormBlockContent,
  },
  campaign_banner: {
    label: "Campaign Banner",
    icon: "Megaphone",
    editorComponent: "SectionContextPanel",
    rendererComponent: "CampaignBannerBlock",
    category: "cta",
    defaultContent: {
      message: "Help us reach our goal",
      cta_text: "Give now",
      cta_url: "/donate",
      dismissible: true,
    } satisfies CampaignBannerBlockContent,
  },
  // NOS-219: dynamic blog feed — settings only, post data is fetched live.
  latest_posts: {
    label: "Latest Posts",
    icon: "FileText",
    editorComponent: "LatestPostsBlockEditor",
    rendererComponent: "LatestPostsBlockRenderer",
    category: "content",
    defaultContent: {
      heading: "Latest Posts",
      count: 3,
      show_images: true,
      view_all: "header",
      view_all_text: "View All",
    } satisfies LatestPostsBlockContent,
  },
}

/** Get config for a block type */
export function getBlockConfig(type: BlockType): BlockTypeConfig {
  const config = BLOCK_REGISTRY[type]
  if (!config) {
    throw new Error(`Unknown block type: ${type}`)
  }
  return config
}

/** Get default content for a block type */
export function getDefaultBlockContent<T extends BlockType>(type: T): BlockContentMap[T] {
  return getBlockConfig(type).defaultContent as BlockContentMap[T]
}

/** All block types that have registered configs */
export function getRegisteredBlockTypes(): BlockType[] {
  return Object.keys(BLOCK_REGISTRY) as BlockType[]
}

/** Block types suitable for the block picker (excludes layout-only like hero/footer in some contexts) */
export function getBlockPickerTypes(): BlockType[] {
  return getRegisteredBlockTypes()
}
